import request from 'graphql-request';
import { AnyStateConfig, fromPromise, setup } from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';

import { bindMvpdMutation } from './graphql/constants';
import { refreshAuthSuiteToken } from './authSuiteRefresh';
import {
    getMvpdBindErrorMessage,
    getMvpdSignInHandoff,
    isTokenInvalidBindError,
} from './signInHandoff';

export type MvpdFlowContext = {
    url: string;
};

export type MvpdBindOutput = {
    bound: boolean;
};

/**
 * Binds the AuthSuite-authenticated MVPD to the just-signed-in account —
 * the TN counterpart of legacy sign-in's registerWithApi. Invoked by the
 * dtc machine's mvpdBind state after a signIn done with the handoff
 * present (P+ only, gated by options.mvpdBindEnabled + the guard). A bind
 * failure rejects with mapped legacy copy so the dtc error action can
 * surface it.
 */
export const mvpdBindActor: FlowEntity = {
    initActor: ({ t }) =>
        fromPromise<MvpdBindOutput>(async () => {
            // Re-read the handoff (URL + localStorage) — self-contained on
            // purpose, so the actor needs no input plumbing from the config.
            const handoff = getMvpdSignInHandoff();
            if (!handoff) {
                // The dtc guard already checked; defensive for direct invokes.
                return { bound: false };
            }
            // Plain request, not fetchQuery: a bind is a mutation and must
            // fire every time, never resolve from the query cache.
            const bind = (authSuiteToken: string) =>
                request(`/api/tn/signup/graphql/`, bindMvpdMutation, {
                    fields: { authSuiteToken },
                });

            try {
                await bind(handoff.authSuiteToken);
            } catch (bindError) {
                // Legacy parity (SignIn.vue authSuiteTokenErrorCallBack →
                // checkAccessStatus → token-watcher re-bind): an invalid token
                // is silently refreshed and the bind retried once. The
                // token-changed guard is legacy's loop protection.
                if (isTokenInvalidBindError(bindError)) {
                    const freshToken = await refreshAuthSuiteToken();
                    if (freshToken && freshToken !== handoff.authSuiteToken) {
                        try {
                            await bind(freshToken);
                            return { bound: true };
                        } catch (retryError) {
                            throw new Error(
                                getMvpdBindErrorMessage(retryError, t, handoff.providerName),
                            );
                        }
                    }
                }
                throw new Error(getMvpdBindErrorMessage(bindError, t, handoff.providerName));
            }
            return { bound: true };
        }),
};

export const mvpdFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router } = rawParams as InitMachineParams;
        const machine = setup({
            types: {
                context: {} as MvpdFlowContext,
                input: {} as { url: string },
            },
            guards: {},
            actions: {
                goToMvpdRoute: ({ context }) => {
                    router.push(context.url);
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                ...input,
            }),
        });

        return machine;
    },
};
